import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { COLLECTIONS, titleKey, type CollectionKey, type Field, type Rec } from '../../shared/schema'
import { ConflictError, createRecord, deleteRecord, openFileUrl, updateRecord, uploadFile, useRecords } from '../lib/api'
import { isoToLocal, localToIso } from '../lib/format'
import { Button, ConfirmButton, Drawer, Icon, useToast } from './ui'

export interface FormProps {
  col: CollectionKey
  record?: Rec | null
  defaults?: Record<string, any>
  open: boolean
  onClose: () => void
  onSaved?: (r: Rec) => void
  title?: string
  /** contenido extra arriba del formulario (resúmenes, acciones) */
  top?: (draft: Record<string, any>) => ReactNode
  /** acciones extra en el pie (duplicar, PDF…) */
  actions?: (rec: Rec | null, draft: Record<string, any>) => ReactNode
  hide?: string[]
}

const emptyFor = (f: Field) => (f.type === 'files' || f.type === 'multi' || (f.type === 'relation' && !f.single) ? [] : '')

export function RecordForm({ col, record, defaults, open, onClose, onSaved, title, top, actions, hide = [] }: FormProps) {
  const schema = COLLECTIONS[col]
  const toast = useToast()
  const [draft, setDraft] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [conflict, setConflict] = useState<Rec | null>(null)
  const initial = useRef<string>('')

  useEffect(() => {
    if (!open) return
    const base: Record<string, any> = {}
    for (const f of schema.fields) base[f.key] = record?.[f.key] ?? defaults?.[f.key] ?? emptyFor(f)
    setDraft(base); setErrors({}); setConflict(null)
    initial.current = JSON.stringify(base)
  }, [open, record?.id, record?.updatedAt])

  const set = (k: string, v: any) => setDraft(d => ({ ...d, [k]: v }))
  const visible = schema.fields.filter(f => !f.hidden && !f.mirror && !hide.includes(f.key) && (!f.showIf || f.showIf.in.includes(draft[f.showIf.key])))
  const sections = useMemo(() => {
    const out: { name: string; fields: Field[] }[] = []
    let last = ''
    for (const f of visible) {
      const name = f.section ?? last
      last = name
      let s = out.find(x => x.name === name)
      if (!s) out.push((s = { name, fields: [] }))
      s.fields.push(f)
    }
    return out
  }, [visible.map(f => f.key).join()])

  async function save(force = false) {
    const errs: Record<string, string> = {}
    for (const f of visible) if (f.required && !String(draft[f.key] ?? '').trim()) errs[f.key] = 'Este campo es obligatorio.'
    for (const f of visible) if (f.type === 'email' && draft[f.key] && !/^\S+@\S+\.\S+$/.test(draft[f.key])) errs[f.key] = 'Revisá el formato del correo.'
    for (const f of visible) if (f.type === 'url' && draft[f.key] && !/^https?:\/\//.test(draft[f.key])) errs[f.key] = 'El enlace tiene que empezar con https://'
    setErrors(errs)
    if (Object.keys(errs).length) { toast('Revisá los campos marcados.', 'error'); return }
    setSaving(true)
    try {
      // solo mandamos lo que cambió (o todo, si es nuevo)
      const before = JSON.parse(initial.current || '{}')
      const patch: Record<string, any> = {}
      for (const f of schema.fields) if (!f.mirror && JSON.stringify(draft[f.key]) !== JSON.stringify(record ? record[f.key] ?? before[f.key] : undefined)) patch[f.key] = draft[f.key]
      const saved = record
        ? (Object.keys(patch).length ? await updateRecord(col, record, patch, { force }) : record)
        : await createRecord(col, { ...defaults, ...draft })
      toast(record ? 'Cambios guardados en Notion' : 'Guardado en Notion')
      onSaved?.(saved)
      onClose()
    } catch (e: any) {
      if (e instanceof ConflictError) setConflict(e.current)
      else toast(e.message, 'error')
    } finally { setSaving(false) }
  }

  const tk = titleKey(col)
  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={record?.ref ?? `Nuevo registro · ${schema.label}`}
      title={title ?? (draft[tk] || (record ? 'Sin título' : `${cap(schema.singular)} sin título`))}
      footer={
        <>
          <div className="foot-left">
            {record && <ConfirmButton label="Eliminar" confirmLabel="Sí, enviar a la papelera de Notion" onConfirm={async () => {
              try { await deleteRecord(col, record.id); toast('Enviado a la papelera de Notion'); onClose() } catch (e: any) { toast(e.message, 'error') }
            }} />}
            {actions?.(record ?? null, draft)}
          </div>
          <div className="foot-right">
            <Button variant="quiet" onClick={onClose}>Cancelar</Button>
            <Button variant="primary" busy={saving} onClick={() => save()} icon="check">Guardar</Button>
          </div>
        </>
      }
    >
      {conflict && (
        <div className="conflict" role="alert">
          <strong>Este registro cambió en Notion mientras lo editabas.</strong>
          <p>{conflict.socia ? `Última edición: ${new Date(conflict.updatedAt!).toLocaleString('es-UY')}.` : 'Alguien lo actualizó hace un momento.'} Podés cargar la versión actual (perdés tus cambios) o guardar la tuya encima.</p>
          <div className="row gap-s">
            <Button size="sm" variant="ghost" onClick={() => { onSaved?.(conflict); onClose() }}>Ver versión actual</Button>
            <Button size="sm" variant="accent" onClick={() => save(true)}>Guardar mis cambios igual</Button>
          </div>
        </div>
      )}
      {top?.(draft)}
      {sections.map(s => (
        <fieldset key={s.name || 'main'} className="form-section">
          {s.name && <legend>{s.name}</legend>}
          <div className="form-grid">
            {s.fields.map(f => (
              <FieldInput key={f.key} col={col} field={f} value={draft[f.key]} onChange={v => set(f.key, v)} error={errors[f.key]} recordId={record?.id} />
            ))}
          </div>
        </fieldset>
      ))}
    </Drawer>
  )
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const wide = (f: Field) => ['longtext', 'files', 'image', 'title'].includes(f.type) || (f.type === 'relation' && !f.single) || f.type === 'multi'

export function FieldInput({ col, field: f, value, onChange, error, recordId }: { col: CollectionKey; field: Field; value: any; onChange: (v: any) => void; error?: string; recordId?: string }) {
  const id = `f-${col}-${f.key}`
  let input: ReactNode
  switch (f.type) {
    case 'longtext':
      input = <textarea id={id} rows={f.key === 'contenido' ? 16 : 4} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={f.placeholder} />
      break
    case 'number': case 'money':
      input = <div className={f.type === 'money' ? 'with-prefix' : ''}>{f.type === 'money' && <span className="prefix">USD</span>}
        <input id={id} type="number" inputMode="decimal" step="any" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder={f.placeholder} /></div>
      break
    case 'select':
      input = <select id={id} value={value ?? ''} onChange={e => onChange(e.target.value)}><option value="">—</option>{f.options!.map(o => <option key={o}>{o}</option>)}</select>
      break
    case 'multi':
      input = <div className="chips-input">{f.options!.map(o => {
        const on = (value ?? []).includes(o)
        return <button key={o} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => onChange(on ? value.filter((x: string) => x !== o) : [...(value ?? []), o])}>{o}</button>
      })}</div>
      break
    case 'date':
      input = <input id={id} type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} />
      break
    case 'datetime':
      input = <input id={id} type="datetime-local" value={isoToLocal(value)} onChange={e => onChange(localToIso(e.target.value))} />
      break
    case 'relation':
      input = <RelationInput id={id} field={f} value={value} onChange={onChange} excludeId={recordId} />
      break
    case 'files':
      input = <FilesInput id={id} field={f} value={value ?? []} onChange={onChange} folder={`${col}/${recordId ?? 'nuevo'}`} />
      break
    case 'image':
      input = <ImageInput id={id} field={f} value={value} onChange={onChange} folder={`${col}/${recordId ?? 'nuevo'}`} />
      break
    case 'checkbox':
      input = <input id={id} type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      break
    case 'json':
      input = <pre className="json">{JSON.stringify(value, null, 2)}</pre>
      break
    default:
      input = <input id={id} type={f.type === 'email' ? 'email' : f.type === 'url' ? 'url' : f.type === 'phone' ? 'tel' : 'text'} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={f.placeholder} className={f.type === 'title' ? 'input-title' : ''} />
  }
  return (
    <div className={`field ${wide(f) ? 'field-wide' : ''} ${error ? 'has-error' : ''}`}>
      <label htmlFor={id}>{f.label}{f.required && <span className="req" aria-hidden="true"> *</span>}</label>
      {input}
      {error ? <small className="field-error">{error}</small> : f.help ? <small className="field-help">{f.help}</small> : null}
    </div>
  )
}

function RelationInput({ id, field, value, onChange, excludeId }: { id: string; field: Field; value: any; onChange: (v: any) => void; excludeId?: string }) {
  const { data } = useRecords(field.relation!)
  const tk = titleKey(field.relation!)
  const opts = data.filter(r => r.id !== excludeId)
  if (field.single) {
    return <select id={id} value={value ?? ''} onChange={e => onChange(e.target.value)}>
      <option value="">— Sin {field.label.toLowerCase()} —</option>
      {opts.map(r => <option key={r.id} value={r.id}>{r[tk] || 'Sin título'}{r.ref ? ` · ${r.ref}` : ''}</option>)}
    </select>
  }
  const list: string[] = value ?? []
  return (
    <div className="relation-multi">
      <div className="chips">{list.map(rid => {
        const r = data.find(x => x.id === rid)
        return <span key={rid} className="chip on">{r?.[tk] ?? '…'}<button type="button" aria-label="Quitar" onClick={() => onChange(list.filter(x => x !== rid))}><Icon name="x" size={12} /></button></span>
      })}</div>
      <select id={id} value="" onChange={e => e.target.value && onChange([...list, e.target.value])}>
        <option value="">+ Agregar {field.label.toLowerCase()}</option>
        {opts.filter(r => !list.includes(r.id)).map(r => <option key={r.id} value={r.id}>{r[tk]}</option>)}
      </select>
    </div>
  )
}

export function FileLink({ value }: { value: string }) {
  const toast = useToast()
  const own = value.includes('/api/archivos/')
  const label = value.startsWith('data:') ? 'Archivo cargado' : decodeURIComponent(value.split('/').pop()?.split('?')[0]?.replace(/^[a-z0-9]+-/, '') || value)
  return (
    <button type="button" className="file-link" onClick={async () => {
      try { const url = await openFileUrl(value); window.open(url, '_blank', 'noopener') } catch (e: any) { toast(e.message, 'error') }
    }}>
      <Icon name={own ? 'shield' : 'external'} size={14} /><span>{label}</span>
    </button>
  )
}

function FilesInput({ id, field, value, onChange, folder }: { id: string; field: Field; value: string[]; onChange: (v: string[]) => void; folder: string }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [url, setUrl] = useState('')
  const canUpload = !!field.bucket
  return (
    <div className="files-input">
      {value.length > 0 && <ul className="file-list">{value.map((v, i) => (
        <li key={i}>
          {/^(data:image|https?:.*\.(jpe?g|jpeg|png|webp|gif)(\?|$))/i.test(v) ? <img src={v} alt="" className="file-thumb" /> : null}
          <FileLink value={v} />
          <button type="button" className="icon-btn sm" aria-label="Quitar archivo" onClick={() => onChange(value.filter((_, j) => j !== i))}><Icon name="x" size={13} /></button>
        </li>
      ))}</ul>}
      <div className="row gap-s wrap">
        {canUpload && (
          <label className={`btn btn-ghost btn-sm ${busy ? 'is-busy' : ''}`}>
            {busy ? <span className="spinner" /> : <Icon name="upload" size={15} />}<span>{busy ? 'Subiendo…' : 'Subir archivos'}</span>
            <input id={id} type="file" multiple hidden accept={field.bucket === 'public' ? 'image/*' : undefined} onChange={async e => {
              const files: File[] = Array.from(e.target.files ?? []); e.target.value = ''
              if (!files.length) return
              setBusy(true)
              try {
                const out = [...value]
                for (const f of files) out.push(await uploadFile(f, field.bucket!, folder))
                onChange(out); toast(`${files.length} archivo${files.length > 1 ? 's' : ''} listo${files.length > 1 ? 's' : ''}. Guardá para registrarlo${files.length > 1 ? 's' : ''} en Notion.`)
              } catch (err: any) { toast(err.message, 'error') } finally { setBusy(false) }
            }} />
          </label>
        )}
        <div className="add-url">
          <input type="url" placeholder="o pegá un enlace (Drive, Canva…)" value={url} onChange={e => setUrl(e.target.value)} aria-label={`Agregar enlace a ${field.label}`} />
          <Button size="sm" variant="quiet" icon="plus" disabled={!/^https?:\/\//.test(url)} onClick={() => { onChange([...value, url]); setUrl('') }}>Agregar</Button>
        </div>
      </div>
    </div>
  )
}

function ImageInput({ id, field, value, onChange, folder }: { id: string; field: Field; value: string; onChange: (v: string) => void; folder: string }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <div className="image-input">
      <div className="image-preview">{value ? <img src={value} alt="" /> : <div className="ph"><Icon name="image" size={26} /></div>}</div>
      <div className="col gap-s">
        <label className="btn btn-ghost btn-sm">
          {busy ? <span className="spinner" /> : <Icon name="upload" size={15} />}<span>{value ? 'Cambiar imagen' : 'Subir imagen'}</span>
          <input id={id} type="file" accept="image/*" hidden onChange={async e => {
            const f = e.target.files?.[0]; e.target.value = ''
            if (!f) return
            setBusy(true)
            try { onChange(await uploadFile(f, field.bucket ?? 'public', folder)) } catch (err: any) { toast(err.message, 'error') } finally { setBusy(false) }
          }} />
        </label>
        {value && <Button size="sm" variant="quiet" icon="x" onClick={() => onChange('')}>Quitar</Button>}
      </div>
    </div>
  )
}
